// Generic hook for entity screens (organizations, properties, etc.)
import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useResponsive } from '@/hooks/common/useResponsive';

interface UseEntityScreenOptions<T> {
  /**
   * CSV headers for export
   */
  csvHeaders: (string | number)[];
  
  /**
   * Function to format table data for CSV export
   */
  formatCsvRow?: (entity: T) => (string | number)[];
}

interface UseEntityScreenReturn<T> {
  // Panel states
  showFormModal: boolean;
  showEditPanel: boolean;
  showCreatePanel: boolean;
  selectedEntity: T | null;
  
  // CSV data
  csvData: (string | number)[][];
  
  // Layout
  layoutMode: 'mobile' | 'desktop' | 'tablet';
  
  // Handlers
  handleEntitySelect: (entity: T) => void;
  handleAddEntity: () => void;
  handleSuccess: () => void;
  handleClose: () => void;
  formatCsvData: (data: T[]) => void;
}

/**
 * Generic hook for managing entity screen state and behavior
 * Reduces duplication across Organizations, Properties, and other entity screens
 */
export const useEntityScreen = <T extends { id: number }>(
  options: UseEntityScreenOptions<T>
): UseEntityScreenReturn<T> => {
  const { csvHeaders, formatCsvRow } = options;
  const { layoutMode } = useResponsive();
  
  // Memoize csvHeaders to prevent infinite loops
  // Using JSON.stringify for csvHeaders since it's an array of primitives
  const stableCsvHeaders = useMemo(() => csvHeaders, [JSON.stringify(csvHeaders)]);
  
  // Use ref to store formatCsvRow to avoid including it in callback dependencies
  const formatCsvRowRef = useRef(formatCsvRow);
  
  // Update ref when formatCsvRow changes
  useEffect(() => {
    formatCsvRowRef.current = formatCsvRow;
  }, [formatCsvRow]);
  
  // Panel states
  const [showFormModal, setShowFormModal] = useState(false);
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<T | null>(null);
  
  // CSV data
  const [csvData, setCsvData] = useState<(string | number)[][]>([stableCsvHeaders]);
  
  /**
   * Handle entity selection (for viewing/editing)
   */
  const handleEntitySelect = useCallback((entity: T) => {
    setSelectedEntity(entity);
    
    // Use side panel on desktop, modal on mobile
    if (layoutMode === 'desktop') {
      setShowEditPanel(true);
    } else {
      setShowFormModal(true);
    }
  }, [layoutMode]);
  
  /**
   * Handle add new entity
   */
  const handleAddEntity = useCallback(() => {
    setSelectedEntity(null);
    
    // Use side panel on desktop, modal on mobile
    if (layoutMode === 'desktop') {
      setShowCreatePanel(true);
    } else {
      setShowFormModal(true);
    }
  }, [layoutMode]);
  
  /**
   * Handle success (refresh and close)
   */
  const handleSuccess = useCallback(() => {
    setShowFormModal(false);
    setShowEditPanel(false);
    setShowCreatePanel(false);
    setSelectedEntity(null);
  }, []);
  
  /**
   * Handle close (without success)
   */
  const handleClose = useCallback(() => {
    setShowFormModal(false);
    setShowEditPanel(false);
    setShowCreatePanel(false);
    setSelectedEntity(null);
  }, []);
  
  /**
   * Format CSV data for export
   */
  const formatCsvData = useCallback((data: T[]) => {
    if (!data || data.length === 0) {
      setCsvData([stableCsvHeaders]);
      return;
    }
    
    if (!formatCsvRowRef.current) {
      setCsvData([stableCsvHeaders]);
      return;
    }
    
    const formattedData = [
      stableCsvHeaders,
      ...data.map(formatCsvRowRef.current)
    ];
    setCsvData(formattedData);
  }, [stableCsvHeaders]);
  
  return {
    // Panel states
    showFormModal,
    showEditPanel,
    showCreatePanel,
    selectedEntity,
    
    // CSV data
    csvData,
    
    // Layout
    layoutMode,
    
    // Handlers
    handleEntitySelect,
    handleAddEntity,
    handleSuccess,
    handleClose,
    formatCsvData,
  };
};

